import { format } from 'date-fns';
import moment from 'moment';

export function langToLid(lang: string): number {
  if (lang === 'en') return 1033;
  if (lang === 'el') return 1032;
  return 1033;
}

export function datestr(ts: string | number | null | undefined): string {
  if (ts !== null && ts !== undefined && ts !== '') {
    const n: number = typeof ts === 'number' ? ts : parseInt(ts, 10);
    return format(n, 'YYYY-MM-DD');
  } else {
    return '';
  }
}

export function formatDate(d: string): string {
  if (d === null || d === undefined || d === '') {
    return '-';
  }
  return new Date(d).toLocaleDateString('El');
}

export function formatDateTime(d: string): string {
  if (d === null || d === undefined || d === '') {
    return '-';
  }
  return new Date(d).toLocaleString('el-GR');
}

export const isArray = (() =>
  Array.isArray ||
  (<T>(x: unknown): x is T[] => x && typeof (x as { length?: number }).length === 'number'))();

export const isArrayLike = <T>(x: unknown): x is ArrayLike<T> =>
  !!x && typeof (x as { length?: number }).length === 'number' && typeof x !== 'function';

export function isDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(+value);
}

// from rxjs
export function isNumeric(val: unknown): val is number | string {
  // parseFloat NaNs numeric-cast false positives (null|true|false|"")
  // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
  // subtraction forces infinities to NaN
  // adding 1 corrects loss of precision from parseFloat (#15100)
  if (isArray(val)) return false;
  if (typeof val !== 'number' && typeof val !== 'string') return false;
  return (val as number) - parseFloat(val as string) + 1 >= 0;
}

export function isFunction(x: unknown): x is (...args: unknown[]) => unknown {
  return typeof x === 'function';
}

export function isPromise(value: unknown): value is PromiseLike<unknown> {
  return (
    !!value &&
    typeof (value as { subscribe?: unknown }).subscribe !== 'function' &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

export const currencyFormat = (num: number | string) =>
  Number(num)
    .toFixed(2) // always two decimal digits
    .replace('.', ',') // replace decimal point character with ,
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.') + ' €'; // use . as a separator

export function parseINIString(data: string) {
  const regex = {
    section: /^\s*\[\s*([^\]]*)\s*\]\s*$/,
    param: /^\s*([^=]+?)\s*=\s*(.*?)\s*$/,
    comment: /^\s*;.*$/,
  };
  const value: Record<string, string | Record<string, string>> = {};
  const lines = data.split(/[\r\n]+/);
  let section: string | null = null;
  lines.forEach(function (line) {
    if (regex.comment.test(line)) {
      return;
    } else if (regex.param.test(line)) {
      const match = line.match(regex.param);
      if (!match) return;
      if (section) {
        const sectionValue = value[section] as Record<string, string>;
        sectionValue[match[1]] = match[2];
      } else {
        value[match[1]] = match[2];
      }
    } else if (regex.section.test(line)) {
      const match = line.match(regex.section);
      if (!match) return;
      value[match[1]] = {};
      section = match[1];
    } else if (line.length == 0 && section) {
      section = null;
    }
  });
  return value;
}

export function removeUtcOffset(dt: Date): Date {
  dt.setHours(dt.getHours() - moment().utcOffset() / 60);
  return dt;
}

/**
 *
 * @param value
 * @param maxDigits max fraction digits
 * @param minDigits min fraction digits
 * @returns
 */
export function formatFloat(
  value: number | string | null | undefined,
  maxDigits: number,
  minDigits = 0,
): string {
  return value !== undefined && value !== null && value !== ''
    ? parseFloat(String(value)).toLocaleString(undefined, {
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits,
      })
    : '';
}

export function removeTypename<T>(obj: T): T {
  if (Array.isArray(obj)) {
    // Recursively clean each item in the array
    return obj.map(removeTypename) as unknown as T;
  } else if (obj && typeof obj === 'object') {
    // Recursively clean each property in the object
    const newObj: Record<string, unknown> = {};
    for (const key in obj as Record<string, unknown>) {
      if (key !== '__typename') {
        newObj[key] = removeTypename((obj as Record<string, unknown>)[key]);
      }
    }
    return newObj as T;
  }
  // If not an object or array, return as is
  return obj;
}

export function formatWeight(value: number | string | null | undefined): string {
  return formatFloat(value, 3);
}
